import { z } from "zod";
import { Id } from "../common/ids.js";
import { LocalizedText } from "../common/localized-text.js";
import { Money } from "../common/money.js";
import { Quantity } from "../common/quantity.js";
import { Timestamp } from "../common/time.js";
import { defineRoute } from "../http/route.js";
import { OrderView } from "./order.view.js";
import { TradeAddress, TradeParties, TradeSide } from "./terms.js";
/** Change orders: both sides agree to change an order (ORD-5, ORD-7, card 059). */
const change = (value) => z.object({ from: value, to: value }).nullable();
export const ChangeOrderChanges = z
    .object({
    lines: z.array(z.object({
        orderLineId: Id,
        productName: LocalizedText,
        skuCode: z.string().nullable(),
        unit: z.string(),
        quantity: change(Quantity),
        unitPrice: change(Money),
    })),
    deliveryAddress: change(TradeAddress),
    confirmationWindowDays: change(z.number().int().positive()),
    leadTimeDays: change(z.number().int().nonnegative()),
    total: z.object({ from: Money, to: Money }),
})
    .meta({ id: "ChangeOrderChanges" });
export const ScheduleAfterView = z
    .object({
    topUp: Money.nullable().describe("An increase: a new item due at once"),
    lowered: z.array(z.object({ seq: z.number().int().positive(), amount: Money })),
    cancelledSeqs: z.array(z.number().int().positive()).describe("Unpaid items a decrease used up"),
    settledRefund: Money.describe("Paid by bank transfer: returned between the parties (ADR 0005)"),
    heldRefund: Money.describe("Held in escrow: refunded through Payment (stage 6)"),
})
    .meta({ id: "ScheduleAfter" });
export const ChangeOrderView = z
    .object({
    id: Id,
    orderId: Id,
    status: z.enum(["proposed", "accepted", "declined", "withdrawn"]),
    proposedBySide: TradeSide,
    reason: z.string().nullable(),
    changes: ChangeOrderChanges,
    scheduleAfter: ScheduleAfterView.describe("As proposed; as applied once accepted"),
    createdAt: Timestamp,
    answeredAt: Timestamp.nullable(),
    actions: z.array(z.enum(["accept", "decline", "withdraw"])).describe("What the caller may do now"),
})
    .meta({ id: "ChangeOrder" });
export const ChangeOrderCardData = z
    .object({
    orderNumber: z.string(),
    ...TradeParties,
    status: ChangeOrderView.shape.status,
    proposedBySide: TradeSide,
    reason: z.string().nullable(),
    changes: ChangeOrderChanges,
})
    .meta({ id: "ChangeOrderCardData" });
const Party = { surface: "tenant", auth: "user", permission: "order:change" };
const ChangeOrderId = z.object({ changeOrderId: Id });
export const proposeChangeOrder = defineRoute({
    ...Party,
    id: "trade.change_orders.create",
    method: "POST",
    path: "/v1/orders/{orderId}/change-orders",
    marks: ["tenant", "idem", "if-match"],
    summary: "Either side proposes changes to an order not yet complete (`If-Match`: the order's version): line quantities and unit prices, the delivery address from the buyer's address book (the buyer only), the confirmation window, and the lead time. One proposal is open at a time; the other side answers",
    prd: ["ORD-5", "ORD-7"],
    request: {
        params: z.object({ orderId: Id }),
        body: z.object({
            lines: z
                .array(z.object({ orderLineId: Id, quantity: Quantity.optional(), unitPrice: Money.optional() }))
                .max(50)
                .default([]),
            deliveryAddressId: Id.optional(),
            confirmationWindowDays: z.number().int().min(1).max(60).optional(),
            leadTimeDays: z.number().int().min(0).max(730).optional(),
            reason: z.string().trim().min(3).max(500),
        }),
    },
    responses: { 201: { description: "The proposal", schema: ChangeOrderView } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "NOT_FOUND",
        "VALIDATION_FAILED",
        "INVALID_TRANSITION",
        "VERSION_CONFLICT",
    ],
});
export const listChangeOrders = defineRoute({
    ...Party,
    permission: "order:read",
    id: "trade.change_orders.list",
    method: "GET",
    path: "/v1/orders/{orderId}/change-orders",
    marks: ["tenant"],
    summary: "The order's change orders, newest first",
    prd: ["ORD-5"],
    request: { params: z.object({ orderId: Id }) },
    responses: { 200: { description: "Change orders", schema: z.object({ items: z.array(ChangeOrderView) }) } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
const answer = (verb, summary) => defineRoute({
    ...Party,
    id: `trade.change_orders.${verb}`,
    method: "POST",
    path: `/v1/change-orders/{changeOrderId}/${verb}`,
    marks: ["tenant", "idem"],
    summary,
    prd: ["ORD-5"],
    request: { params: ChangeOrderId },
    responses: { 200: { description: "The order", schema: OrderView } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND", "INVALID_TRANSITION"],
});
export const acceptChangeOrder = answer("accept", "The other side accepts: the lines, totals, address, window, and lead time change, and the schedule follows (a top-up due at once for an increase; unpaid items lowered first for a decrease). A new order document follows (card 060). Refused when the order has moved so the change no longer fits");
export const declineChangeOrder = answer("decline", "The other side declines; the order stays as it is");
export const withdrawChangeOrder = answer("withdraw", "The side that proposed withdraws it");
export const changeOrderRoutes = [
    proposeChangeOrder,
    listChangeOrders,
    acceptChangeOrder,
    declineChangeOrder,
    withdrawChangeOrder,
];
//# sourceMappingURL=change-order.routes.js.map